import "./ApiKeyTable.scss";

import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { CellProps, Column } from "react-table";

import { ActionCell } from "../../../../components/ActionCell/ActionCell";
import {
  DateColumn,
  DefaultColumn,
  KeyNameColumn,
} from "../../../../components/CustomizedTable/Columns";
import { PaginatedTable } from "../../../../components/CustomizedTable/PaginatedTable";
import { ConfirmModal } from "../../../../components/CustomModal/ConfirmModal";
import { TableCellSm, TableLineSm } from "../../../../components/TableLineSm";
import { TableSearch } from "../../../../components/TableSearch";
import { useMediaQueryContext } from "../../../../contexts/mediaQueryProvider/MediaQueryProvider";
import { useOrganizationContext } from "../../../../contexts/organizations/organizationsContext";
import type {
  IDeleteKeyRequest,
  IEditQueryParams,
  IKey,
} from "../../../../hooks/reactQueryHooks/useKeys";
import {
  useDeleteKey,
  useEditKey,
} from "../../../../hooks/reactQueryHooks/useKeys";
import { useCheckAccess } from "../../../../hooks/useCheckAccess";
import type { IData } from "../../../../types";
import { shortString } from "../../../../utils/StringFormatting";
import { EditApiKeyForm } from "../forms/EditApiKeyForm";

interface IProps {
  items: IKey[];
  isLoading?: boolean;
  totalCount: number;
  pageSize: number;
}

const ApiKeyTable: React.FC<IProps> = ({
  items,
  isLoading,
  totalCount,
  pageSize,
}) => {
  const [currentApiKeyData, setCurrentApiKeyData] = useState<IKey | null>();
  const [rowToDelete, setRowToDelete] = useState<string>("");
  const [confirmModalVisible, setConfirmModalVisible] =
    useState<boolean>(false);
  const { isLg } = useMediaQueryContext();

  const handleApiKeyCancel = () => {
    setRowToDelete("");
    setConfirmModalVisible(false);
  };

  const { currentOrganization } = useOrganizationContext();
  const noSubscriptionError = `Deleting API keys is disabled while there is no active subscription for the ${currentOrganization?.name} organization.`;

  const { displayError } = useCheckAccess(noSubscriptionError, false);

  const deleteMutation = useDeleteKey();
  const handleDeleteItem = (id: string) => {
    const deleteRequest: IDeleteKeyRequest = {
      organizationId: currentOrganization?.id || "",
      keyId: id,
    };
    deleteMutation.mutate(deleteRequest);
  };

  const editMutation = useEditKey();

  const handleApiKeySubmit = () => {
    if (!currentApiKeyData) return;
    const { id, state = "", name } = currentApiKeyData;
    const editRequest: IEditQueryParams = {
      organizationId: currentOrganization?.id || "",
      keyId: id,
      name,
      state,
    };
    editMutation.mutate(editRequest);
  };

  useEffect(() => {
    if (!deleteMutation.isSuccess) {
      return;
    }
    setConfirmModalVisible(false);
    setRowToDelete("");
  }, [deleteMutation.isSuccess]);

  useEffect(() => {
    if (!editMutation.isSuccess) {
      return;
    }
    handleEditApiKeyCancel();
  }, [editMutation.isSuccess]);

  const handleEditApiKeyCancel = () => {
    setCurrentApiKeyData(null);
  };

  const handleApiKeyDelete = (rowToDelete: string) => {
    setRowToDelete(rowToDelete);
    setConfirmModalVisible(true);
  };

  const renderName = (props: CellProps<IData>) => (
    <KeyNameColumn
      name={props.row?.original?.name}
      state={props.row?.original?.state}
    />
  );

  const renderActions = useCallback(
    (props: CellProps<IData>) => (
      <ActionCell
        onDeleteClick={() => {
          handleApiKeyDelete(props?.row?.original?.id);
          setConfirmModalVisible(true);
          deleteMutation.reset();
        }}
        onEditClick={() => setCurrentApiKeyData(props.row?.original as IKey)}
      />
    ),
    [deleteMutation],
  );

  const renderKey = (props: CellProps<IData>) => (
    <DefaultColumn data={shortString(props.row?.original?.key, 17)} />
  );
  const renderAppName = (props: CellProps<IData>) => (
    <DefaultColumn
      data={props.row?.original?.appName ? props.row?.original?.appName : "n/a"}
    />
  );
  const renderAppType = (props: CellProps<IData>) => (
    <DefaultColumn data={props.row?.original?.apiKeyType} />
  );
  const renderDate = (props: CellProps<IData>) => (
    <DateColumn date={props.row?.original?.сreationDateTime} type="short" />
  );

  const renderSM = (props: CellProps<IData>) => (
    <TableLineSm
      title={
        <div className="d-flex w-auto align-items-center">
          {renderName(props)}
        </div>
      }
      controls={renderActions(props)}
      colspan={4}
    >
      <TableCellSm title="Api key">{renderKey(props)}</TableCellSm>
      <TableCellSm title="Project">{renderAppName(props)}</TableCellSm>
      <TableCellSm title="Type">{renderAppType(props)}</TableCellSm>
      <TableCellSm title="Creation date">{renderDate(props)}</TableCellSm>
    </TableLineSm>
  );

  const columns: Column<IData>[] = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        minWidth: 140,
        width: 200,
        Cell: renderName,
      },
      {
        Header: "api key",
        accessor: "key",
        minWidth: 140,
        width: 170,
        Cell: renderKey,
      },
      {
        Header: "project",
        accessor: "appName",
        minWidth: 120,
        width: 170,
        Cell: renderAppName,
      },
      {
        Header: "Type",
        accessor: "type",
        minWidth: 120,
        width: 200,
        Cell: renderAppType,
      },
      {
        Header: "Creation date",
        accessor: "сreationDateTime",
        minWidth: 120,
        width: 200,
        Cell: renderDate,
      },
      {
        Header: "",
        id: "edit",
        minWidth: 50,
        width: 50,
        maxWidth: 50,
        Cell: renderActions,
      },
    ],
    [renderActions],
  );

  const columnsSm: Column<IData>[] = [
    {
      Header: "API Keys",
      accessor: "Key",
      Cell: renderSM,
    },
  ];

  return (
    <>
      {confirmModalVisible && (
        <ConfirmModal
          visible={true}
          onCancel={handleApiKeyCancel}
          onSubmit={() => handleDeleteItem(rowToDelete)}
          title="Delete API key"
          text="This CogCache API key will be deleted from your organization. You will no longer be able to use this for your API requests. This action cannot be undone. Are you sure you want to delete this key permanently?"
          okText="Delete"
          isProcessing={deleteMutation.isLoading}
          displayError={displayError}
        />
      )}

      {currentApiKeyData && (
        <EditApiKeyForm
          organization={currentOrganization}
          apiKey={currentApiKeyData}
          isProcessing={editMutation.isLoading}
          isError={editMutation.isError}
          handleKeyChange={setCurrentApiKeyData}
          onCancel={handleEditApiKeyCancel}
          onSubmit={handleApiKeySubmit}
        />
      )}

      <div className="custom-header-wrapper">
        <div className="align-items-center position-relative">
          <TableSearch
            label={
              <>
                <strong>Search</strong> all API keys
              </>
            }
            placeholder="Type to filter..."
          />
        </div>
      </div>

      <PaginatedTable
        columns={isLg ? columnsSm : columns}
        data={items}
        isLoading={isLoading}
        totalItems={totalCount}
        itemsPerPage={pageSize}
      />
    </>
  );
};

export default ApiKeyTable;
